import React, { useEffect, useState } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import format from 'date-fns/format';
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays';
import isBefore from 'date-fns/isBefore';
import { useHistory } from 'react-router-dom';
import {
  COMPLETED,
  HIRED,
  INTRODUCED,
  OFFERED, ON_HOLD,
  REJECTED,
  SOURCED,
} from '../../../../constants/statuses';
import { useAppSelector } from '../../../../store/hooks';
import { ADMIN_ROLE, CLIENT_ROLE, RECRUITER_ROLE } from '../../../../constants/roles';
import { CANDIDATES } from '../../../../constants/routes';
import TooltipPrompt from '../../../../UI/tootlip';
import { CandidateInfo, CandidatesStagesStatuses, Recruiter } from '../../../../types';
import { ReactComponent as ArrowRight } from '../../../../icons/arrow-right.svg';
import {
  Wrapper,
  CardBody,
  DateText,
  NameWrapper,
  Name,
  Title,
  Interview,
  Drag,
  Company,
  CompanyLogo,
  CompanyName,
  GHWarning,
  Tags,
  OffSyncBannerContainer
} from './styles';
import {
  INTERVIEW_ID_PARAM,
  STAGE_PARAM,
} from '../../../../constants/queryParams';
import CandidateStatusDropdown from "../../CandidateStatusDropdown";
import styled from 'styled-components';
import { getCompanyRecruiters } from '../../../../api/recruiter';

interface ItemInfo extends React.HTMLAttributes<HTMLDivElement> {
  info: CandidateInfo;
  index: number;
  column: string;
  candidateStagesStatuses: CandidatesStagesStatuses[];
  onOffSyncClicked: (info: CandidateInfo) => void;
}

// Define styled components for the footer pill
const CreatedByFooterWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: left; /* Center content in the footer */
  border-top: 1px solid #dae5ec; /* Add border like Prompt */
  padding: 7px; /* Add padding like Prompt */
  margin-top: auto; /* Push to bottom if needed, depends on container */
`;

const CreatedByFooterText = styled.span`
  display: inline-block;
  font-size: 0.7rem; /* Consistent small size */
  color: #627d95;
  font-weight: 500;
  background-color: #f0f2f5;
  padding: 0.05em 0.5em;
  border-radius: 1em;
  line-height: 1.3;
  margin-left: 0.3rem;
`;

const Item = ({
  info,
  column,
  index,
  candidateStagesStatuses,
  onOffSyncClicked,
  ...rest
}: ItemInfo) => {
  const [t] = useTranslation();
  const history = useHistory();
  const { user } = useAppSelector((state) => state.user);
  const tasks = info.interviews[0]?.current_stage?.tasks;

  const isClient = user?.role === CLIENT_ROLE;
  const isRecruiter = user?.role === RECRUITER_ROLE;
  const isAdmin = user?.role === ADMIN_ROLE;

  const mainInterview = tasks?.find((task) => task.is_planning_task);
  const earn = '';

  const isOfferStage = info.interviews[0].status === OFFERED;
  const isHired = info.interviews[0].status === HIRED;
  const isOnHold = info.interviews[0].status === ON_HOLD;
  const hasOffer =
    info.interviews[0].current_offer?.status === COMPLETED &&
    user?.role === CLIENT_ROLE;

  const isOffSync = info?.interviews[0]?.is_blocked;
  const isAtsErrored = info?.interviews[0]?.ats_error !== '';
  const showOffSyncWarning = !isRecruiter && (isOffSync || isAtsErrored);
  const offSyncTooltip = isAtsErrored
    ? t(`ats.OFF_SYNC_TOOLTIP_ERROR_${info?.interviews[0]?.ats_error}`)
    : t('ats.OFF_SYNC_TOOLTIP_CLIENT');

  const isDraggable =
    info.interviews[0].current_stage?.status === COMPLETED &&
    user?.role === CLIENT_ROLE &&
    !isOfferStage;


  const isDragDisabled = column === HIRED || isAdmin;

  const [hasSourcers, setHasSourcers] = useState(false);

  useEffect(() => {
    const fetchSourcers = async () => {
      if (isRecruiter && user?.company?.id) {
        try {
          const res = await getCompanyRecruiters(user.company.id, 1, 50);
          const recruiters: Recruiter[] = res.data.results || [];
          setHasSourcers(recruiters.some(r => r.is_sourcer));
        } catch {
          setHasSourcers(false);
        }
      }
    };
    fetchSourcers();
  }, [isRecruiter, user?.company?.id]);

  const handleClick = (item: CandidateInfo) => {
    const search = new URLSearchParams(history.location.search);
    if ([INTRODUCED, SOURCED].includes(column)) {
      search.set('history', info.id);
      search.set('interview_id', info.interviews[0].id);
      const query = search.toString();
      history.push(CANDIDATES + '?' + query);
    } else if (column === REJECTED) {
      search.set('history', info.id);
      search.set('interview_id', info.interviews[0].id);
      const query = search.toString();
      history.push(CANDIDATES + '?' + query);
    } else if (column === OFFERED) {
      if (!isRecruiter && !isClient) return; // skip for admin
      if (!(isDraggable || (isOfferStage && hasOffer))) {
        // open interview stage modal
        search.set(INTERVIEW_ID_PARAM, info.interviews[0]?.id);
        search.set(STAGE_PARAM, column);
        const query = search.toString();
        history.push(CANDIDATES + '?' + query);
      }
    } else if (column === HIRED) {
    } else if (column === ON_HOLD) {
    } else {
      if (!isRecruiter && !isClient) return; // skip for admin

      // open interview stage modal
      search.set(INTERVIEW_ID_PARAM, info.interviews[0].id);
      search.set(STAGE_PARAM, info.interviews[0].current_stage.stage_name);
      const query = search.toString();
      history.push(CANDIDATES + '?' + query);
    }
  };

  const diffInDays =
    (mainInterview &&
      differenceInCalendarDays(
        new Date(mainInterview.time_from),
        new Date(),
      )) ||
    0;

  return (
    <Draggable
      draggableId={`${info.id}&${column}&${index}`}
      index={index}
      isDragDisabled={isDragDisabled}
    >
      {(provided) => (
        <Wrapper
          {...rest}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          ref={provided.innerRef}
          onClick={() => handleClick(info)}
          style={{ ...rest.style, ...provided.draggableProps.style }}
        >
          {showOffSyncWarning && (
            <OffSyncBannerContainer>
              <TooltipPrompt title={offSyncTooltip}>
                <GHWarning onClick={(e) => { onOffSyncClicked(info); e.stopPropagation(); }}>{t('ats.OFF_SYNC_BADGE')}</GHWarning>
              </TooltipPrompt>
            </OffSyncBannerContainer>
          )}
          <TooltipPrompt title={showOffSyncWarning ? "" : t('KANBAN_ITEM_TOOLTIP')}>
            <CardBody>
              <NameWrapper>
                <Name
                  onClick={(e) => {
                    const query = new URLSearchParams(history.location.search);
                    query.set('history', info.id);
                    query.set('interview_id', info.interviews[0].id);
                    e.stopPropagation();
                    e.preventDefault();
                    history.push({ search: query.toString() });
                  }}
                >
                  {info.name}
                </Name>
                {!(user?.role === RECRUITER_ROLE) && <Tags>
                  {(isDraggable || (isOfferStage && hasOffer) || isOnHold) &&
                    column !== HIRED &&
                    column !== REJECTED &&
                    user?.role === CLIENT_ROLE ? (
                    <Drag>{t('DRAG')}</Drag>
                  ) : user?.role === RECRUITER_ROLE ? (
                    <Drag>{earn}</Drag>
                  ) : (
                    ''
                  )}
                </Tags>}
              </NameWrapper>
              <Title>{info.interviews[0].job.title}</Title>
              <CandidateStatusDropdown onClick={(e: any) => {
                e.stopPropagation();
              }} interview={info.interviews[0]} statuses={candidateStagesStatuses} isClient={isClient} customClass={'kanban-status'} />
              {user?.role === RECRUITER_ROLE && (
                <Company>
                  <CompanyLogo>
                    <img
                      src={`${info.interviews[0].job.company.image
                        ? info.interviews[0].job.company.image
                        : process.env.PUBLIC_URL + '/images/company-logo.png'
                        }`}
                      alt=""
                    />
                  </CompanyLogo>
                  <CompanyName>{info.interviews[0].job.company.name}</CompanyName>
                </Company>
              )}
              {!(isHired || isOfferStage || isOnHold || column === REJECTED) && (
                <Interview>
                  {mainInterview && mainInterview.time_from && (
                    <Title>
                      <ArrowRight />
                      {t('INTERVIEW')}:<span>&nbsp;</span>
                      <DateText
                        className={
                          isBefore(new Date(mainInterview.time_from), new Date())
                            ? 'danger'
                            : ''
                        }
                      >
                        {diffInDays === -1 ? (
                          <>
                            {t('YESTERDAY')},{' '}
                            {format(new Date(mainInterview.time_from), ' kk:mm')}
                          </>
                        ) : diffInDays === 0 ? (
                          <>
                            {t('TODAY')},{' '}
                            {format(new Date(mainInterview.time_from), ' kk:mm')}
                          </>
                        ) : diffInDays === 1 ? (
                          <>
                            {t('TOMORROW')},{' '}
                            {format(new Date(mainInterview.time_from), ' kk:mm')}
                          </>
                        ) : (
                          format(
                            new Date(mainInterview.time_from),
                            'MMM d, kk:mm',
                          )
                        )}
                      </DateText>
                    </Title>
                  )}
                </Interview>
              )}
            </CardBody>
          </TooltipPrompt>

          {isRecruiter && hasSourcers && user && !user.is_sourcer && info.created_by ? (
            <CreatedByFooterWrapper>
              <CreatedByFooterText>
                {info.created_by.id === user.id
                  ? t('CREATED_BY_ME')
                  : t('CREATED_BY_USER', { name: info.created_by.name })}
              </CreatedByFooterText>
            </CreatedByFooterWrapper>
          ) : null}
        </Wrapper>
      )}
    </Draggable>
  );
};

export default Item;
